import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Instagram, MapPin, Send, Award } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');
const API = `${BACKEND_URL}/api`;

const Contato = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: 'easeOut' },
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contato-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
              Contato
            </h1>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] max-w-3xl mx-auto">
              Entre em contato para agendar sua consulta ou tirar dúvidas. Estou pronto para ajudá-lo em
              sua jornada de transformação.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div {...fadeUp}>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-8">
                Informações de Contato
              </h2>

              <div className="space-y-6 mb-12">
                <a
                  href="https://wa.me/5521967611301"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5 hover:border-white/20 transition-colors group"
                  data-testid="contact-whatsapp"
                >
                  <Phone className="text-[#C0C0C0] group-hover:text-white transition-colors flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">WhatsApp</h3>
                    <p className="text-[#A3A3A3] group-hover:text-[#E5E5E5] transition-colors">
                      (21) 96761-1301
                    </p>
                    <p className="text-sm text-[#A3A3A3] mt-1">Atendimento de segunda a sexta, 9h às 18h</p>
                  </div>
                </a>

                <a
                  href="mailto:contato@nutriaron.com"
                  className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5 hover:border-white/20 transition-colors group"
                  data-testid="contact-email"
                >
                  <Mail className="text-[#C0C0C0] group-hover:text-white transition-colors flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">E-mail</h3>
                    <p className="text-[#A3A3A3] group-hover:text-[#E5E5E5] transition-colors">
                      contato@nutriaron.com
                    </p>
                    <p className="text-sm text-[#A3A3A3] mt-1">Resposta em até 24 horas</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5">
                  <MapPin className="text-[#C0C0C0] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Localização</h3>
                    <p className="text-[#A3A3A3]">
                      Av. João Cabral de Mello Neto, 850 — 10º Andar<br />
                      Torre North, Bloco 1 — Barra da Tijuca<br />
                      Rio de Janeiro/RJ — CEP 22775-057<br />
                      Atendimento presencial e online
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5">
                  <Award className="text-[#C0C0C0] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Registros Profissionais</h3>
                    <p className="text-[#A3A3A3]">
                      Nutricionista — CRN RJ 26100311<br />
                      Ed. Física — CREF 050596
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold tracking-normal mb-6">Redes Sociais</h3>
                <a
                  href="https://instagram.com/nutriaron"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-[#121212] border border-white/5 hover:border-white/20 hover:bg-white hover:text-black transition-all"
                  data-testid="contact-instagram"
                >
                  <Instagram size={20} />
                  <span className="font-medium">@nutriaron</span>
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
              <div className="bg-[#121212] border border-white/10 p-8">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-8">
                  Envie uma mensagem
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Nome completo
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none transition-colors"
                      data-testid="contact-input-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      E-mail
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none transition-colors"
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Telefone/WhatsApp
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none transition-colors"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Mensagem
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none transition-colors resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-white text-black font-bold uppercase tracking-widest px-8 py-4 hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black"></div>
                    ) : (
                      <>
                        <Send size={20} />
                        Enviar mensagem
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Perguntas Frequentes
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: 'Qual a melhor forma de entrar em contato?',
                a: 'O WhatsApp é a forma mais rápida. Respondo geralmente em poucos minutos durante o horário comercial. Você também pode usar o formulário acima ou e-mail.',
              },
              {
                q: 'Atende em qual região?',
                a: 'Atendimento presencial na Barra da Tijuca, Rio de Janeiro/RJ (Av. João Cabral de Mello Neto, 850 — Torre North). Para protocolo online, atendo clientes de todo o Brasil e exterior.',
              },
              {
                q: 'Quanto tempo demora para conseguir agendar?',
                a: 'Geralmente consigo agendar protocolos na mesma semana. Em períodos de alta demanda, pode levar até 10 dias.',
              },
              {
                q: 'Posso tirar dúvidas antes de agendar?',
                a: 'Sim! Entre em contato sem compromisso. Ficarei feliz em esclarecer suas dúvidas sobre os serviços.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0A0A0A] border border-white/5 p-8"
                data-testid={`contact-faq-${index}`}
              >
                <h3 className="text-xl font-semibold tracking-normal mb-3">{faq.q}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;